<script setup>
import { ref, computed, inject} from 'vue'
import { useWorkspaceStore } from '@stores/WorkspaceData';
const wpStore = useWorkspaceStore()
import { simulationService } from '@requests/Main/simulation.service';
import SimulationCharts from './SimulationCharts.vue';
import Select from 'primevue/select';
const toast = inject('toast')

const Tar = computed(() => wpStore.experiment.Tar)
const Sat = computed(() => wpStore.experiment.Sat)
const Plan = computed(() => wpStore.experiment.Plan)
const Record = computed(() => Plan.value?.bst?.records?.[0] || null)
const Result = computed(() => wpStore.simulation)

const shootingModeLabel = {PHOTO_NON_AGILE: 'Фотосъёмка не манёвренная', PHOTO_AGILE: 'Фотосъёмка манёвренная', VIDEO: 'Видеосъёмка', ROUTE: 'Маршрутная съёмка'}
const stepLabel = {STEP_1_20: '1/20 с', STEP_1_10: '1/10 с', STEP_1_5: '1/5 с', STEP_1_4: '1/4 с', STEP_1_2: '1/2 с', STEP_1: '1 с'}
const stepOptions = Object.entries(stepLabel).map(([value, label]) => ({value, label}))
// Шаг для запуска моделирования — отдельно от горизонта (горизонт не меняется).
// Передаётся в теле запроса моделирования (simulation.service.js). null — как в горизонте
const simulationStep = computed({
  get: () => wpStore.simulationStep ?? wpStore.time?.simulationStep,
  set: (value) => { wpStore.simulationStep = value },
})
// A — разворот на координаты цели и удержание цели, B — на углы ориентации
const modeInfo = {
  A: {label: 'A — на координаты цели', p1: 'Долгота цели', p2: 'Широта цели'},
  B: {label: 'B — на углы ориентации', p1: 'Тангаж', p2: 'Крен'},
}

const formatTime = (unix) => unix == null ? null : new Date(unix * 1000).toLocaleString('ru-RU')
const formatNumber = (value, unit = '') => value == null ? null : `${Number(value).toLocaleString('ru-RU', {maximumFractionDigits: 3})}${unit}`
const formatDeviation = (sec) => sec == null ? null : `${sec > 0 ? '+' : ''}${formatNumber(sec, ' с')}`

const cards = computed(() => {
  const mode = modeInfo[Record.value?.mode]
  return [
    {title: 'Заявка', icon: 'pi pi-flag', rows: [
      ['Вид съёмки', shootingModeLabel[Tar.value?.shootingMode] || Tar.value?.shootingMode],
      ['Цель', Tar.value ? `${formatNumber(Tar.value.latitude, '°')} ш., ${formatNumber(Tar.value.longitude, '°')} д.` : null],
      ['КА', Sat.value?.name],
    ]},
    {title: 'План', icon: 'pi pi-calendar', rows: [
      ['Начало разворота', formatTime(Plan.value?.turnaround?.turnaroundStart)],
      ['Начало съёмки', formatTime(Plan.value?.shootingTime?.shootingStart)],
      ['Окончание съёмки', formatTime(Plan.value?.shootingTime?.shootingEnd)],
    ]},
    {title: 'Полётное задание', icon: 'pi pi-send', rows: [
      ['Начало', formatTime(Record.value?.time)],
      ['Режим', mode?.label || Record.value?.mode],
      [mode?.p1 || 'Параметр 1', formatNumber(Record.value?.parameter1, '°')],
      [mode?.p2 || 'Параметр 2', formatNumber(Record.value?.parameter2, '°')],
    ]},
    {title: 'Горизонт моделирования', icon: 'pi pi-clock', rows: [
      ['Начало', formatTime(wpStore.time?.horizonStart)],
      ['Окончание', formatTime(wpStore.time?.horizonEnd)],
      // третий элемент — строка редактируется селектором (шаг для запуска моделирования)
      ['Шаг', stepLabel[simulationStep.value] || simulationStep.value, 'step'],
    ]},
  ]
})

const resultRows = computed(() => {
  const r = Result.value
  if(!r) return []
  const actual = r.actualShooting
  return [
    ['Фактическое начало съёмки', actual ? formatTime(actual.start) : 'съёмка не выполнена', formatDeviation(actual?.startDeviation)],
    ['Фактическое окончание съёмки', actual ? formatTime(actual.end) : 'съёмка не выполнена', formatDeviation(actual?.endDeviation)],
    ['Угол луча', formatNumber(r.beamAngle, '°'), null],
    ['Точек моделирования', formatNumber(r.points?.length), null],
  ]
})

const running = ref(false)
const RunSimulation = async () => {
  running.value = true
  toast.info('Запускаем моделирование полётного задания')
  const result = await simulationService.Run()
  running.value = false
  if(result) toast.success('Моделирование выполнено')
}
</script>

<template>
<section class="ModellingPage">
  <div class="ModellingToolbar">
    <Button label="Выполнить моделирование" icon="pi pi-play" iconPos="left" raised
      :loading="running" :disabled="running || !Record" @click="RunSimulation"/>
    <small v-if="!Record" class="ModellingHint">Сначала сформируйте полётное задание на вкладке «Полётное задание»</small>
  </div>

  <div class="ModellingGrid">
    <div v-for="card in cards" :key="card.title" class="ModellingCard">
      <div class="ModellingCardTitle"><i :class="card.icon"></i>{{ card.title }}</div>
      <dl>
        <template v-for="[label, value, edit] in card.rows" :key="label">
          <dt>{{ label }}</dt>
          <dd v-if="edit === 'step'" class="ModellingEdit">
            <Select v-model="simulationStep" :options="stepOptions" optionLabel="label" optionValue="value"
              size="small" class="ModellingStep" aria-label="Шаг моделирования"/>
          </dd>
          <dd v-else :class="{empty: value == null}">{{ value ?? '—' }}</dd>
        </template>
      </dl>
    </div>

    <div class="ModellingCard ModellingResult">
      <div class="ModellingCardTitle"><i class="pi pi-chart-line"></i>Результат моделирования</div>
      <dl v-if="resultRows.length">
        <template v-for="[label, value, deviation] in resultRows" :key="label">
          <dt>{{ label }}</dt>
          <dd :class="{empty: value == null}">
            {{ value ?? '—' }}
            <span v-if="deviation" class="ModellingDeviation">{{ deviation }}</span>
          </dd>
        </template>
      </dl>
      <p v-else class="ModellingEmpty">Моделирование не выполнялось</p>
    </div>

    <SimulationCharts v-if="Result?.points?.length"/>
  </div>
</section>
</template>

<style>
.ModellingPage{
  padding: 10px;
  text-align: left;
}
.ModellingStep{
  min-width: 110px;
  text-align: left;
}
.ModellingToolbar{
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.ModellingHint{
  color: var(--p-text-muted-color);
}
.ModellingGrid{
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 10px;
}
.ModellingCard{
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-content-border-radius);
  padding: 12px 14px;
}
.ModellingCardTitle{
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--p-primary-color);
  margin-bottom: 8px;
}
.ModellingCard dl{
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 16px;
  row-gap: 6px;
  margin: 0;
}
.ModellingCard dt{
  color: var(--p-text-muted-color);
}
.ModellingCard dd{
  margin: 0;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.ModellingCard dd.empty{
  color: var(--p-text-muted-color);
}
.ModellingDeviation{
  display: block;
  color: var(--p-text-muted-color);
  font-size: 0.9em;
}
.ModellingEmpty{
  margin: 0;
  color: var(--p-text-muted-color);
}
</style>
